import type { Contact, CallContact } from '@/services/types/Contact'

interface CallDisplayInput {
  activeCall: ComputedRef<ConferenceEntry | null>
  heldCall: ComputedRef<ConferenceEntry | null>
  ringingCall: ComputedRef<ConferenceEntry | null>
  incomingRingingCallId: Ref<string | null>
  isConnecting: Ref<boolean>
}

export function useCallDisplay ({ activeCall, heldCall, ringingCall, incomingRingingCallId, isConnecting }: CallDisplayInput) {
  const isConference = computed(() => (activeCall.value?.conferenceParticipants.length ?? 0) > 1)
  const isIncomingRinging = computed(() => !!ringingCall.value && !activeCall.value && !heldCall.value)
  const isNewIncomingCall = computed(() => !!ringingCall.value && (!!activeCall.value || !!heldCall.value))
  const isOnHold = computed(() => !!heldCall.value)

  const ringingContact = computed(() => ringingCall.value?.conferenceParticipants[0]?.contact ?? null)
  const activeParticipants = computed(() => activeCall.value?.conferenceParticipants ?? ringingCall.value?.conferenceParticipants ?? [])
  const primaryActiveContact = computed(() => activeParticipants.value[0]?.contact ?? null)
  const isPrimaryActiveOnHold = computed(() => activeParticipants.value[0]?.isOnHold ?? false)
  const heldParticipants = computed(() => heldCall.value?.conferenceParticipants ?? [])
  const primaryHeldContact = computed(() => heldParticipants.value[0]?.contact ?? null)

  const callTitle = computed(() => {
    if (isIncomingRinging.value) { return 'Someone is Calling You' }
    if (isConference.value) { return 'Conference Call' }
    return 'Call in Progress'
  })

  // Combined label for multi-party calls: the lone name for one, "A & B" for two, "A & N more" for three or more.
  const participantName = (p: ConferenceParticipant) => p?.contact?.name || p?.contact?.phone || 'Unknown'
  const combinedLabel = (participants: ConferenceParticipant[]) => {
    if (participants.length === 0) { return '' }
    const first = participantName(participants[0])
    if (participants.length === 1) { return first }
    if (participants.length === 2) {
      return `${first} & ${participantName(participants[1])}`
    }
    return `${first} & ${participants.length - 1} more`
  }
  const activeCallLabel = computed(() => combinedLabel(activeParticipants.value))
  const heldCallLabel = computed(() => combinedLabel(heldParticipants.value))

  const callInProgress = computed(
    () => !!activeCall.value || !!incomingRingingCallId.value?.startsWith('CF')
  )

  const dialerOpen = ref(false)
  const callSidebarOpen = ref(false)
  // Without isConnecting the sidebar briefly closes and reopens between "dial" and
  // the active call.
  watch([callInProgress, dialerOpen, isConnecting], ([inProgress, dialer, connecting]) => {
    callSidebarOpen.value = inProgress || dialer || connecting
  })

  const outgoingCall = computed(() =>
    activeCall.value?.type === CallType.OUTGOING ? activeCall.value.call : null
  )
  const currentOutgoingContact = ref<Contact | CallContact | null>(null)
  const lastCompletedOutgoingContact = ref<Contact | CallContact | null>(null)
  watch(activeCall, (call) => {
    if (call?.type === CallType.OUTGOING) {
      currentOutgoingContact.value = call.conferenceParticipants[0]?.contact ?? null
    }
  })
  watch(outgoingCall, (call, previousCall) => {
    if (!call && previousCall && currentOutgoingContact.value) {
      lastCompletedOutgoingContact.value = currentOutgoingContact.value
      currentOutgoingContact.value = null
    }
  })

  return {
    isConference,
    isIncomingRinging,
    isNewIncomingCall,
    isOnHold,
    ringingContact,
    activeParticipants,
    heldParticipants,
    primaryHeldContact,
    primaryActiveContact,
    isPrimaryActiveOnHold,
    callTitle,
    activeCallLabel,
    heldCallLabel,
    callInProgress,
    dialerOpen,
    callSidebarOpen,
    lastCompletedOutgoingContact
  }
}
